<template>
  <img
    v-if="!maintainRatio"
    ref="image"
    :style="imgStyleObject"
    :src="imgSrc"
    :class="{ auto: fluid }"
    :datasrc="singleSrcUrl"
    :sizes="sizesArray"
    :alt="altText"
    :height="height"
    :width="width"
  />
  <div v-else class="ns-image__background" :style="divStyleObject">
    <div
      :style="divBgStyleObject"
      class="ns-image__background-image"
      :class="[{ 'ns-image__contain': imageFit === 'contain' }, ratioClass]"
      :title="altText"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { ImageService } from "@nuskin/ns-util";

// The ns-image tag puts the heavy lifting of image optimization on the browser,
// allowing the browser to choose the most efficient image to download, while
// also taking advantage of some of the nice css object-fit and object-position
// properties.

export default {
  name: "NsImage",
  props: {
    /**
     * Provides the alt tag for screen readers
     */
    alt: {
      type: String,
      default: ""
    },
    /**
     * Fallback image if first fails
     */
    fallbackSrc: {
      type: String,
      default: ""
    },
    /**
     * Makes the width of the image 'auto' instead of '100%'
     */
    fluid: {
      type: Boolean,
      default: false,
      reflectToAttribute: true
    },
    height: {
      type: String,
      default: ""
    },
    /**
     * Sets image fit within the container (css object-fit). Valid values are:
     * contain, cover, fill, none, scale-down
     */
    imageFit: {
      type: String,
      default: "cover"
    },
    /**
     * Sets image position (css object-position). Valid values are strings like
     * 'left center', 'center bottom' and so on. This value is passed directly
     * to the object-position property. We recommend not using 'px px' or '% %'
     * strings to avoid strange behavior.
     */
    imagePosition: {
      type: String,
      default: "center"
    },
    /**
     * Aspect ratios for the image
     * Values are: 1x1 / 4x3 / 16x9 / 21x9
     */
    maintainRatio: {
      type: String,
      default: ""
    },
    /**
     * directs the srcset string to not include thumbnail sized images.
     */
    // TODO: defaut should be true
    noThumbnail: Boolean,
    /**
     * if the size of image is known ahead of time or wanted, but the rendition
     * url is not present this will request a specific image size or smaller
     * than the number requested (if not smallest). images are close to the
     * following dimensions, however they maintain aspect ratio when resized and
     * may not be square as the numbers suggest. values are the following
     * numbers:
     *
     * -1: original image
     *  0: thumbnail   48x48
     *  1: thumbnail   140x100
     *  2: small       319x319
     *  3: phone       540x540
     *  4: tablet      720x720
     *  5: large       1280X1280
     */
    sizeLock: {
      type: Number,
      default: -1
    },
    /**
     * Overrides the default image breakpoint suggestions in the sizes array
     */
    sizesArray: {
      type: String,
      default:
        "(max-device-width: 480px) 100px, (max-device-width: 1024px) 300px, 1000px"
    },
    /**
     * url for the image without a renditions path param. If renditions are
     * specified in this url, it will pass through as the single src. Use
     * careful judgement if making such a request.
     */
    src: {
      type: String,
      default: ""
    },
    /**
     * directs the srcset string to include only thumbnail sized images.
     */
    thumbnailOnly: {
      type: Boolean,
      default: false
    },
    /**
     * create a source set for the given image url.
     */
    useSrcSet: {
      type: Boolean,
      default: false,
      reflectToAttribute: true
    },
    width: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      altText: this.alt,
      imgSrc: this.src,
      singleSrcUrl: this.src,
      divBgStyleObject: {
        backgroundImage: this.src
      }
    };
  },
  computed: {
    divStyleObject() {
      return {
        height: this.height,
        width: this.width
      };
    },
    imgStyleObject() {
      return { objectFit: this.imageFit, objectPosition: this.imagePosition };
    },
    ratioClass() {
      return this.maintainRatio ? `ns-image__ratio-${this.maintainRatio}` : "";
    }
  },
  watch: {
    src() {
      this.setImage();
    }
  },
  mounted() {
    this.setImage();
  },
  methods: {
    setAltText(imageUrl) {
      if (!this.alt && typeof imageUrl === "string") {
        this.altText = imageUrl
          .substring(imageUrl.lastIndexOf("/") + 1, imageUrl.lastIndexOf("."))
          .replace(/-/g, " ");
      }
    },
    setImage() {
      // Protect against an undefined this.originalUrl
      // clean up invalid urls (no spaces allowed)
      this.imgSrc = this.src ? this.src.replace(/ /g, "%20") : "";
      // for div as background-image
      if (this.maintainRatio) {
        const tempstring = this.useSrcSet
          ? ImageService.getSizedURL(this.imgSrc, this.sizeLock)
          : this.imgSrc.length
          ? this.imgSrc
          : this.fallbackSrc;
        this.divBgStyleObject.backgroundImage = `url(${tempstring})`;
        this.setAltText(tempstring);
        return;
      }

      this.setAltText(this.imgSrc);

      const el = this.$refs.image;

      if (el) {
        el.onload = () => {
          el.removeAttribute("datasrc");
        };

        //always set the src attribute as a fallback
        this.imgSrc = ImageService.getSizedURL(this.imgSrc, this.sizeLock);

        if (this.useSrcSet && this.sizeLock < 0) {
          el.setAttribute(
            "srcset",
            ImageService.createSRCSET(this.imgSrc, this.noThumbnail)
          );
        }
        el.onerror = () => {
          // el.removeAttribute("srcset");
          if (!this.useSrcSet && this.fallbackSrc) {
            this.imgSrc = this.fallbackSrc;
          }
        };
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/other";

img {
  opacity: 1;
  transition: opacity 0.15s ease-in;
  height: 100%;
  width: 100%;

  &.auto {
    width: auto;
  }
}

// img[datasrc],
// img[datasrcset] {
//   opacity: 0;
// }

// USING DIV WITH BACKGROUND IMG

.ns-image__background {
  height: 100%;
  width: 100%;
  overflow: hidden;
}

.ns-image__background-image {
  opacity: 1;
  padding-top: map.get(other.$otherUtils, aspect-ratio-1-1);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  min-height: 100%;
  // min-height: 0;
  width: 100%;
  transition: opacity 0.15s ease-in;

  &.ns-image__contain {
    background-size: contain;
  }

  &.ns-image__ratio-1x1 {
    padding-top: map.get(other.$otherUtils, aspect-ratio-1-1);
  }

  &.ns-image__ratio-4x3 {
    padding-top: map.get(other.$otherUtils, aspect-ratio-4-3);
  }

  &.ns-image__ratio-16x9 {
    padding-top: map.get(other.$otherUtils, aspect-ratio-16-9);
  }

  &.ns-image__ratio-21x9 {
    padding-top: map.get(other.$otherUtils, aspect-ratio-21-9);
  }
}
</style>
